/**
 * A failed run, and what the person looking at it should actually do.
 *
 * The screen used to answer this with one sentence and a primary button
 * reading "Yeniden Çalıştır". For the failure customers hit most - the cameras
 * could not be solved because the photographs do not overlap enough - that
 * button re-runs the same photographs through the same solver and fails the
 * same way. It was the product's confident suggestion to waste twenty minutes.
 *
 * So failures are classified by what fixes them:
 *
 * - **user_action** - the capture is the problem. The primary action goes to
 *   the photographs, the coverage or the help that explains overlap. Re-running
 *   is offered, second, and described as what it is.
 * - **infrastructure** - ours. Nobody is asked to re-shoot anything, and
 *   re-running is the reasonable first move.
 * - **terminal** - neither is established. Nothing is promised.
 *
 * The failure code never reaches the screen, and neither does the stage, the
 * engine or anything the worker wrote for a log.
 */

import { Link } from "react-router-dom";

import type { ReconstructionJob } from "@/api/types";
import { Button, Callout } from "@/components/ui";
import { RECONSTRUCTION_TERMS, reconstructionFailureMessage } from "@/i18n/terms";
import { paths } from "@/app/paths";
import { ElapsedReading } from "./RunProgress";
import { failureClassOf, failureHelpTopic } from "./reconstructionState";

export function RunFailure({
  job,
  projectId,
  onRerun,
  rerunning,
  rerunDisabled,
}: {
  job: ReconstructionJob;
  projectId: string;
  onRerun: () => void;
  rerunning: boolean;
  rerunDisabled: boolean;
}) {
  const kind = failureClassOf(job.failure_code);
  const topic = failureHelpTopic(job.failure_code);

  return (
    <Callout tone="caution" title="3D oluşturma tamamlanamadı">
      <div className="stack-tight">
        <p>{reconstructionFailureMessage(job.failure_code)}</p>
        <ElapsedReading job={job} />

        {kind === "infrastructure" && (
          // The sentence that stops somebody re-shooting a capture that was
          // fine. This failure is ours and the screen says so plainly.
          <p className="muted">
            Fotoğraflarınızla ilgili bir sorun tespit edilmedi; işlem sırasında sistem
            kaynaklı bir hata oluştu.
          </p>
        )}

        {job.failure_summary && <p className="faint">{job.failure_summary}</p>}

        <div className="row">
          {kind === "user_action" ? (
            <>
              {/*
                Where the problem is, not where the button was. Overlap and
                blur are decided in the photographs and in the capture angles,
                and both screens already exist.
              */}
              <Link className="btn btn--primary" to={paths.projectSessions(projectId)}>
                Fotoğrafları kontrol et
              </Link>
              <Link className="btn btn--secondary" to={paths.projectCoverage(projectId)}>
                Çekim açılarını kontrol et
              </Link>
              {topic && (
                <Link className="btn btn--secondary" to={paths.helpTopic(topic)}>
                  Nasıl çekmeliyim?
                </Link>
              )}
            </>
          ) : (
            <>
              <Button
                variant={kind === "infrastructure" ? "primary" : "secondary"}
                onClick={onRerun}
                busy={rerunning}
                disabled={rerunDisabled}
              >
                {RECONSTRUCTION_TERMS.rerunAction}
              </Button>
              {topic && (
                <Link className="btn btn--secondary" to={paths.helpTopic(topic)}>
                  Yardımı aç
                </Link>
              )}
            </>
          )}
        </div>

        {kind === "user_action" && (
          <div className="stack-tight">
            {/*
              Offered, and honest about what it will do. Nothing changed since
              the last attempt means the same inputs and, for a deterministic
              failure, the same result.
            */}
            <Button onClick={onRerun} busy={rerunning} disabled={rerunDisabled}>
              {RECONSTRUCTION_TERMS.rerunAction}
            </Button>
            <span className="faint">
              Fotoğraflar değişmeden yeniden çalıştırmak büyük olasılıkla aynı sonucu verir.
            </span>
          </div>
        )}
      </div>
    </Callout>
  );
}
